/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file index.js
 * @description DashboardStaff Services
 */

// Core
import endpoints from '@nephro/config/endpoints';
import { externalConnector } from '@nephro/core/connector';

// Endpoints
const {
  recommendedByScheduling,
  antibodyAntiHBs,
  indiBloodPressureMonitoring,
  indiGlucoseMonitoring,
  albuminuriaProgress,
  albuminuriaF13,
  TFGDeterioration,
  nutritionalIndicator,
  hemoglobin,
  potassium,
  missedAppointmentsByStage,
  proteinuria,
  metabolicControlDiabetes,
  phosphorus,
  albumina,
  pthIndicator,
  seroStatusHepatitisB,
  risk,
  bloodPressure,
  vaccineScheduling,
  postVaccineAntiHbs,
  riskRegister,
  lossHepatitisImmunity,
  correlationRiskRateProgression,
  treatmentOptions,
  psychosocialRiskPsychology,
  psychosocialRisk,
  access,
  schedulingAlert,
  proteinEnergeticWear,
  generalSocialTSocialRisk,
  nursingEducation,
  assessmentInPsychology,
  dissentWithoutPsychology,
  patientEvents,
  periodicityAttention,
  constructionAccessAssessment,
  generalSocialRiskRegister,
  followUpAbsenteeismAdherence
} = endpoints.dashboardStaff;

const { versia } = endpoints;

export class DashboardStaffService {
  constructor(http) {
    this.http = http;
  }

  /**
   * Get Recommended By Scheduling info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get recommended Scheduling patient.
   */
  getRecommendedByScheduling = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${recommendedByScheduling.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get recommended Scheduling patient list
   */
  getRecommendedBySchedulingList = async (clinicId, date, limit, offset) => {
    const queryParam = `?date=${date}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${recommendedByScheduling.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params query params
   * @returns {Object} - Get recommended Scheduling patient list
   */
  getRecommendedBySchedulingListPlus = async (params) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${recommendedByScheduling.plus}`;
    return this.http.get(`${url}?${queryParams}`);
  };

  /**
   * Get Missed Appointments by Stage info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get recommended Scheduling patient.
   */
  getMissedAppointmentsByStage = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${missedAppointmentsByStage.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} stage - stage
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get recommended Scheduling patient list
   */
  getMissedAppointmentsByStageList = async (clinicId, stage, limit, offset) => {
    const queryParam = `?estadio=${stage}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${missedAppointmentsByStage.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params query params
   * @returns {Object} - Get recommended Scheduling patient list
   */
  getMissedAppointmentsByStageListPlus = async (params) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${missedAppointmentsByStage.plus}`;
    return this.http.get(`${url}?${queryParams}`);
  };

  /**
   * Get Antibody AntiHBs info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Antibody Anti HBs
   */
  getAntibodyAntiHBs = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${antibodyAntiHBs.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get IndiBlood Pressure Monitoring info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get IndiBlood Pressure Monitoring
   */
  getIndiBloodPressureMonitoring = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${indiBloodPressureMonitoring.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Indi Glucose Monitoring info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Glucose Monitoring
   */
  getIndiGlucoseMonitoring = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${indiGlucoseMonitoring.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Antibody Anti HBs List
   */
  getAntibodyAntiHBsList = async (
    clinicId,
    clasificacionSeleccionada,
    limit,
    offset
  ) => {
    const queryParam = `?clasificacionSeleccionada=${clasificacionSeleccionada}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${antibodyAntiHBs.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params query params of body
   * @returns {Object} - Get Antibody Anti HBs List Plus
   */
  getAntibodyAntiHBsListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${antibodyAntiHBs.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Indi Blood Pressure Monitoring List
   */
  getIndiBloodPressureMonitoringList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${indiBloodPressureMonitoring.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} params - Params
   * @returns {Object} - Get Indi Blood Pressure Monitoring List
   */
  getIndiBloodPressureMonitoringListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${indiBloodPressureMonitoring.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Indi Blood Pressure Monitoring List
   */
  getIndiGlucoseMonitoringList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${indiGlucoseMonitoring.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} params - Params
   * @returns {Object} - Get Indi Blood Pressure Monitoring List
   */
  getIndiGlucoseMonitoringListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${indiGlucoseMonitoring.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Albuminuria Progress info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Albuminuria Progress
   */
  getAlbuminuriaProgress = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${albuminuriaProgress.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Albuminuria Progress List
   */
  getAlbuminuriaProgressList = async (
    clinicId,
    variacionSeleccionada,
    limit,
    offset
  ) => {
    const queryParam = `?variacionSeleccionada=${variacionSeleccionada}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${albuminuriaProgress.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params query params of body
   * @returns {Object} - Get Albuminuria Progress List Plus
   */
  getAlbuminuriaProgressListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${albuminuriaProgress.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Albuminuria F13 info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Albuminuria Progress
   */
  getAlbuminuriaF13 = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${albuminuriaF13.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} shift - shift
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Albuminuria Progress List
   */
  getAlbuminuriaF13List = async (clinicId, shift, limit, offset) => {
    const queryParam = `?albuminuriaSeleccionada=${shift}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${albuminuriaF13.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Query params
   * @returns {Object} - Get Albuminuria Progress List
   */
  getAlbuminuriaF13ListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${albuminuriaF13.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Dissent Without Psychology Assessment info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Albuminuria Progress
   */
  getDissentWithoutPsychologyAssessmentF36 = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${dissentWithoutPsychology.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Dissent Without Psychology Assessment info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Albuminuria Progress
   */
  getDissentWithoutPsychologyAssessmentF36ListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${dissentWithoutPsychology.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} shift - shift
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Albuminuria Progress List
   */
  getDissentWithoutPsychologyAssessmentF36List = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${dissentWithoutPsychology.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get TFGDeterioration info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get TFG Deterioration
   */
  getTFGDeterioration = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${TFGDeterioration.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get TFG Deterioration List
   */
  getTFGDeteriorationList = async (
    clinicId,
    deterioroSeleccionado,
    limit,
    offset
  ) => {
    const queryParam = `?deterioroSeleccionado=${deterioroSeleccionado}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${TFGDeterioration.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Params object
   * @returns {Object} - Get TFG Deterioration List
   */
  getTFGDeteriorationListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${TFGDeterioration.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Nutritional Indicator info by clinic id
   * @param { number } clinicId - stage.
   * @param { string } stage - Clinic id.
   * @return { Object } - Get Nutritional Indicator
   */
  getNutritionalIndicator = async (clinicId, stage) => {
    const queryParam = `?estadio=${stage}&idClinic=${clinicId}`;
    const url = `${nutritionalIndicator.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} stage - stage
   * @param {string} clasificacionSeleccionada - clasificacionSeleccionada
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Nutritional Indicator List
   */
  getNutritionalIndicatorList = async (
    clinicId,
    stage,
    clasificacionSeleccionada,
    limit,
    offset
  ) => {
    const queryParam = `?clasificacionSeleccionada=${clasificacionSeleccionada}&estadio=${stage}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${nutritionalIndicator.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - params for queryparam
   * @returns {Object} - Get Nutritional Indicator List
   */
  getNutritionalIndicatorListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${nutritionalIndicator.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Proteinuria Indicator info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Proteinuria Indicator
   */
  getProteinuriaIndicator = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${proteinuria.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Proteinuria Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get Proteinuria Indicator
   */
  getProteinuriaIndicatorListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${proteinuria.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Proteinuria Indicator detail info by clinic id and detail identificator
   * @param { string } clinicId - Clinic id.
   * @param {string} proteinuriaIdentifier - proteinuriaIdentifier
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @return { Object } - Get Proteinuria Indicator
   */
  getProteinuriaIndicatorDetail = async (
    clinicId,
    proteinuriaIdentifier,
    limit,
    offset
  ) => {
    const queryParam = `?clasificacionSeleccionada=${proteinuriaIdentifier}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${proteinuria.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Blood pressure Indicator info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get blood pressure Indicator
   */
  getBloodPressureIndicator = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${bloodPressure.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Blood pressure Indicator info by clinic id
   * @param { string } clinicId - Clinic id.
   * @param { string } classification - blood pressure classification.
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @return { Object } - Get blood pressure Indicator
   */
  getBloodPressureIndicatorList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${bloodPressure.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Blood pressure Indicator info by clinic id
   * @param { Object } params - Clinic id.
   * @return { Object } - Get blood pressure Indicator
   */
  getBloodPressureIndicatorListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${bloodPressure.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Hemoglobin info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Hemoglobin
   */
  getHemoglobin = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${hemoglobin.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Hemoglobin List
   */
  getHemoglobinList = async (
    clinicId,
    clasificacionSeleccionada,
    limit,
    offset
  ) => {
    const queryParam = `?clasificacionSeleccionada=${clasificacionSeleccionada}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${hemoglobin.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - params for query params
   * @returns {Object} - Get Hemoglobin List
   */
  getHemoglobinListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${hemoglobin.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Potassium info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Potassium
   */
  getPotassium = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${potassium.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Potassium List
   */
  getPotassiumList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${potassium.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Params
   * @returns {Object} - Get Potassium List
   */
  getPotassiumListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${potassium.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Metabolic Control Diabetes info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Potassium
   */
  getMetabolicControlDiabetes = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${metabolicControlDiabetes.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Potassium List
   */
  getMetabolicControlDiabetesList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${metabolicControlDiabetes.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params query params of body
   * @returns {Object} - Get getMetabolicControlDiabetesListPlus
   */
  getMetabolicControlDiabetesListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${metabolicControlDiabetes.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Phosphorus info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Phosphorus
   */
  getPhosphorus = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${phosphorus.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Phosphorus List
   */
  getPhosphorusList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${phosphorus.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Params
   * @returns {Object} - Get Phosphorus List
   */
  getPhosphorusListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${phosphorus.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Albumina info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Albumina
   */
  getAlbumina = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${albumina.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Albumina List
   */
  getAlbuminaList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?clasificacionSeleccionada=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${albumina.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - params
   * @returns {Object} - Get Albumina List
   */
  getAlbuminaListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${albumina.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Pth info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Albumina
   */
  getPthIndicator = async (clinicId, stage) => {
    const queryParam = `?idClinic=${clinicId}&estadio=${stage}`;
    const url = `${pthIndicator.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Albumina List
   */
  getPthIndicatorList = async (
    clinicId,
    classification,
    stage,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&estadio=${stage}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${pthIndicator.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Params
   * @returns {Object} - Get Albumina List
   */
  getPthIndicatorListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${pthIndicator.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get SeroStatusHepatitisB info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get SeroStatusHepatitisB
   */
  getSeroStatusHepatitisB = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${seroStatusHepatitisB.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get SeroStatusHepatitisB List
   */
  getSeroStatusHepatitisBList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${seroStatusHepatitisB.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get SeroStatusHepatitisB List
   */
  getSeroStatusHepatitisBListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${seroStatusHepatitisB.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Risk info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Risk
   */
  getRisk = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${risk.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {string} date - date
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Risk List
   */
  getRiskList = async (clinicId, classification, date, limit, offset) => {
    const queryParam = `?classification=${classification}&month=${date}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${risk.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params pagination offset
   * @returns {Object} - Get Risk List
   */
  getRiskListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${risk.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get VaccineScheduling info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get VaccineScheduling
   */
  getVaccineScheduling = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${vaccineScheduling.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} vaccineMonth - vaccine Month
   * @param {string} dose - dose
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get VaccineScheduling List
   */
  getVaccineSchedulingList = async (
    clinicId,
    vaccineMonth,
    dose,
    limit,
    offset
  ) => {
    const queryParam = `?dose=${dose}&vaccineMonth=${vaccineMonth}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${vaccineScheduling.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get VaccineScheduling info by dto object
   * @param { object } response - json response fil.
   * @return { Object } - Get VaccineScheduling
   */
  getVaccineSchedulingListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${vaccineScheduling.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get PostVaccineAntiHbs info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get PostVaccineAntiHbs
   */
  getPostVaccineAntiHbs = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${postVaccineAntiHbs.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get PostVaccineAntiHbs List
   */
  getPostVaccineAntiHbsList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${postVaccineAntiHbs.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Params
   */
  getPostVaccineAntiHbsListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${postVaccineAntiHbs.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get RiskRegister info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get RiskRegister
   */
  getRiskRegister = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${riskRegister.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get RiskRegister List
   */
  getRiskRegisterList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${riskRegister.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - params object
   * @returns {Object} - Get RiskRegister List
   */
  getRiskRegisterListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${riskRegister.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get LossHepatitisImmunity info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get LossHepatitisImmunity
   */
  getLossHepatitisImmunity = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${lossHepatitisImmunity.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get LossHepatitisImmunity List
   */
  getLossHepatitisImmunityList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${lossHepatitisImmunity.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} params - Clinic id
   * @returns {Object} - Get LossHepatitisImmunity List
   */
  getLossHepatitisImmunityListPlus = async (params) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${lossHepatitisImmunity.plus}`;
    return this.http.get(`${url}?${queryParams}`);
  };

  /**
   * Get CorrelationRiskRateProgression info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get CorrelationRiskRateProgression
   */
  getCorrelationRiskRateProgression = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${correlationRiskRateProgression.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get CorrelationRiskRateProgression Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get CorrelationRiskRateProgression Indicator
   */
  getCorrelationRiskRateProgressionListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${correlationRiskRateProgression.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {string} proggression - proggression
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get CorrelationRiskRateProgression List
   */
  getCorrelationRiskRateProgressionList = async (
    clinicId,
    classification,
    proggression,
    limit,
    offset
  ) => {
    const queryParam = `?progression=${proggression}&classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${correlationRiskRateProgression.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get TreatmentOptions info by clinic id
   * @param { string } clinicId - Clinic id.
   * @param {string} classification - classification
   * @return { Object } - Get TreatmentOptions
   */
  getTreatmentOptions = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${treatmentOptions.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get TreatmentOptions Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get Access Indicator
   */
  getTreatmentOptionsListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${treatmentOptions.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - Treatment option selected
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get TreatmentOptions List
   */
  getTreatmentOptionsList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?idClinic=${clinicId}&limit=${limit}&offset=${offset}&classification=${classification}`;
    const url = `${treatmentOptions.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Psychosocial Risk info by clinic id
   * @param { string } clinicId - Clinic id.
   * @param {string} month - month
   * @return { Object } - Get Psychosocial Risk
   */
  getPsychosocialRiskPsychology = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${psychosocialRiskPsychology.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} month - month
   * @param {string} riskParam - riskParam
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Psychosocial Risk List
   */
  getPsychosocialRiskPsychologyList = async (
    clinicId,
    month,
    riskParam,
    limit,
    offset
  ) => {
    const queryParam = `?month=${month}&idClinic=${clinicId}&limit=${limit}&offset=${offset}&risk=${riskParam}`;
    const url = `${psychosocialRiskPsychology.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get psychosocialRiskPsychology Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get Access Indicator
   */
  getPsychosocialRiskPsychologyListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${psychosocialRiskPsychology.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Psychosocial Risk info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Psychosocial Risk
   */
  getPsychosocialRisk = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${psychosocialRisk.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Psychosocial Risk List
   */
  getPsychosocialRiskList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${psychosocialRisk.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get psychosocialrisk info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get psysocialrisk Indicator
   */
  getPsychosocialRiskListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${psychosocialRisk.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Access info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get Access
   */
  getAccess = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${access.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Access Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get Access Indicator
   */
  getAccessListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${access.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Access List
   */
  getAccessList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${access.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get SchedulingAlert info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get SchedulingAlert
   */
  getSchedulingAlert = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${schedulingAlert.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get SchedulingAlert List
   */
  getSchedulingAlertList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${schedulingAlert.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params pagination offset
   * @returns {Object} - Get SchedulingAlert List
   */
  getSchedulingAlertListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${schedulingAlert.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get ProteinEnergeticWear info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get ProteinEnergeticWear
   */
  getProteinEnergeticWear = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${proteinEnergeticWear.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get ProteinEnergeticWear List
   */
  getProteinEnergeticWearList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${proteinEnergeticWear.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params pagination offset
   * @returns {Object} - Get ProteinEnergeticWear List
   */
  getProteinEnergeticWearListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${proteinEnergeticWear.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get GeneralSocialTSocialRisk info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getGeneralSocialTSocialRisk = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${generalSocialTSocialRisk.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get GeneralSocialTSocialRisk List
   */
  getGeneralSocialTSocialRiskList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${generalSocialTSocialRisk.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get general social risk info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get psysocialrisk Indicator
   */
  getGeneralSocialTSocialRiskListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${generalSocialTSocialRisk.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Assessment in Psychology info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getAssessmentInPsychology = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${assessmentInPsychology.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Assessment in Psychology List
   */
  getAssessmentInPsychologyList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${assessmentInPsychology.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Nursing Education info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getNursingEducation = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${nursingEducation.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Nursing Education List
   */
  getNursingEducationList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${nursingEducation.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get Access Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get Access Indicator
   */
  getNursingEducationListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${nursingEducation.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Patient Events info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getPatientEvents = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${patientEvents.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Patient Events  List
   */
  getPatientEventsList = async (clinicId, classification, limit, offset) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${patientEvents.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params pagination offset
   * @returns {Object} - Get Patient Events  List
   */
  getPatientEventsListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${patientEvents.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Periodicity Attention info by clinic id
   * @param { string } clinicId - Clinic id.
   * @param {number} frequency - frequency
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getPeriodicityAttention = async (clinicId, frequency) => {
    const queryParam = `?idClinic=${clinicId}&frequency=${frequency}`;
    const url = `${periodicityAttention.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} frequency - frequency
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Periodicity Attention  List
   */
  getPeriodicityAttentionList = async (
    clinicId,
    classification,
    frequency,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&frequency=${frequency}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${periodicityAttention.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get ConstructionAccessAssessment info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get ConstructionAccessAssessment
   */
  getConstructionAccessAssessment = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${constructionAccessAssessment.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get ConstructionAccessAssessment List
   */
  getConstructionAccessAssessmentList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${constructionAccessAssessment.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get constructionAccessment Indicator info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get Access Indicator
   */
  getConstructionAccessAssessmentListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${constructionAccessAssessment.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get General Social Risk Register info by clinic id
   * @param { string } clinicId - Clinic id.
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getGeneralSocialRiskRegister = async (clinicId) => {
    const queryParam = `?idClinic=${clinicId}`;
    const url = `${generalSocialRiskRegister.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get General Social Risk Register List
   */
  getGeneralSocialRiskRegisterList = async (
    clinicId,
    classification,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${generalSocialRiskRegister.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * Get general social risk info by by dto object
   * @param { Object } request valid json object.
   * @return { Object } - Get psysocialrisk Indicator
   */
  getGeneralSocialRiskRegisterListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${generalSocialRiskRegister.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get Follow Up Absenteeism Adherence info by clinic id
   * @param { string } clinicId - Clinic id.
   * @param {number} phase - phase
   * @return { Object } - Get GeneralSocialTSocialRisk
   */
  getFollowUpAbsenteeismAdherence = async (clinicId, phase) => {
    const queryParam = `?idClinic=${clinicId}&phase=${phase}`;
    const url = `${followUpAbsenteeismAdherence.base}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {number} clinicId - Clinic id
   * @param {string} classification - classification
   * @param {number} phase - phase
   * @param {number} limit pagination limit
   * @param {number} offset pagination offset
   * @returns {Object} - Get Follow Up Absenteeism Adherence List
   */
  getFollowUpAbsenteeismAdherenceList = async (
    clinicId,
    classification,
    phase,
    limit,
    offset
  ) => {
    const queryParam = `?classification=${classification}&phase=${phase}&idClinic=${clinicId}&limit=${limit}&offset=${offset}`;
    const url = `${followUpAbsenteeismAdherence.list}`;
    return this.http.get(`${url}${queryParam}`);
  };

  /**
   * @param {Object} params - Params
   * @returns {Object} - Get Follow Up Absenteeism Adherence List
   */
  getFollowUpAbsenteeismAdherenceListPlus = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    const url = `${followUpAbsenteeismAdherence.plus}`;
    return this.http.get(`${url}?${queryParam}`);
  };

  /**
   * Get clinics list by entity id
   * @param { number } entityId - Entity id.
   * @return { Object } - Get Proteinuria Indicator
   */
  getAdmonCodesByEntity = async (entityId) => {
    const url = `${versia.clinics}/${entityId}`;
    return this.http.get(url);
  };

  /**
   * Get last update version
   * @return { Object } - Get object with the last version update
   */
  getLastUpdate = async () => {
    const url = `${versia.lastUpdate}`;
    return this.http.get(url);
  };
}

export default new DashboardStaffService(externalConnector);
